import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import useHasPermissions from '@/modules/current-user/permissions';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type MutationOptions,
  type QueryOptions,
} from '@/modules/query/utils';

import { SUPPLIER_PROFILE_SUMMARY_QUERY } from './queryKeys';

export type ProfileSummaryStatus = 'enqueued' | 'in_progress' | 'error';

const PENDING_STATUSES: ProfileSummaryStatus[] = ['enqueued', 'in_progress'];

export function getIsPendingStatus(status: ProfileSummaryStatus | undefined): boolean {
  return status !== undefined && PENDING_STATUSES.includes(status);
}

export type ProfileSummary = {
  exec: string;
  content: string;
};

export type ProfileSummaryResponse = {
  id: string;
  canRegenerate: boolean;
  // Absent until a summary has been generated.
  createdAt?: string;
  createdBy?: string;
  status?: ProfileSummaryStatus;
  summary: ProfileSummary | null;
};

const ENQUEUED_POLL_INTERVAL_MS = 5_000;

export function useProfileSummary(supplierID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const { hasAnyOfPermissions, Permission } = useHasPermissions();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_PROFILE_SUMMARY_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ProfileSummaryResponse>(
          'GET',
          `/suppliers/${queryKey[1]}/profile-summary`,
        );
        return res.data;
      },
      enabled: () =>
        hasAnyOfPermissions(Permission.ClientsSuppliersView) &&
        hasAnyOfPermissions(Permission.ClientsRisksView) &&
        hasAnyOfPermissions(Permission.ClientsSuppliersNotesView),
      refetchInterval(q) {
        return getIsPendingStatus(q.state.data?.status) ? ENQUEUED_POLL_INTERVAL_MS : false;
      },
    }),
  );

  const summary = computed(() => query.data.value?.summary ?? null);
  const createdAt = computed(() => query.data.value?.createdAt ?? null);
  const status = computed(() => query.data.value?.status);
  const canRegenerate = computed(() => query.data.value?.canRegenerate ?? false);

  return { query, summary, createdAt, status, canRegenerate };
}

export function useGenerateProfileSummary(
  options?: MutationOptions<ProfileSummaryResponse, { supplierID: string }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ supplierID }) {
        const res = await request<ProfileSummaryResponse>(
          'POST',
          `/suppliers/${supplierID}/profile-summary/generate`,
        );
        return res.data;
      },
      onMutate({ supplierID }) {
        queryClient.setQueryData<ProfileSummaryResponse>(
          [SUPPLIER_PROFILE_SUMMARY_QUERY, supplierID],
          (old) => ({
            id: old?.id ?? '',
            canRegenerate: false,
            status: 'enqueued',
            summary: null,
          }),
        );
        return null;
      },
      onSuccess(data, { supplierID }) {
        queryClient.setQueryData<ProfileSummaryResponse>(
          [SUPPLIER_PROFILE_SUMMARY_QUERY, supplierID],
          data,
        );
      },
    }),
  );
}
